import { useState, useCallback } from 'react';
import type { NavItem } from '../Breadcrumb';

export interface Level {
  key: string;
  next?: Level;
}

export interface Hierarchy {
  type: 'root' | 'children';
  labelField: string;
  valueField: string;
  drillDownField: string;
  partitionBy: string | ((item: Record<string, string>) => string);
  filter?: string;
  next?: Level
}

function findNext(key: string, levels: Level) {
  if (levels.key === key) {
    return levels;
  }
  if (levels.next) {
    return findNext(key, levels.next);
  }
}

function formatLabel(value: any) {
  return Array.isArray(value) ? value.join('') : value.toString();
}

export function useInteraction({
  rootNavItem,
  drillDownLevels,
  createLevel,
  disabledDrillDownKeys,
  onSelect,
  onResetCallback,
  }: {
    rootNavItem: NavItem;
    drillDownLevels: Level;
    createLevel: (level: Level) => Hierarchy;
    disabledDrillDownKeys?: string[];
    onSelect?: (payload: Array<{
      dimension?: string;
      row: { name: Array<string | undefined>; value: Array<string | undefined> };
    }>) => void;
    onResetCallback?: () => void;
  }
) {
  const [navItems, setNavItems] = useState<NavItem[]>([rootNavItem]);
  const [levelInfo, setLevelInfo] = useState<Hierarchy>(createLevel(drillDownLevels));

  const onNavigate = useCallback((item: NavItem) => {
    const idx = navItems.findIndex(i => i.key === item.key);
    const newNavItems = navItems.slice(0, idx + 1);
    const nextLevel = findNext(item.key, drillDownLevels);
    const next = createLevel(nextLevel);

    setLevelInfo(next);
    setNavItems(newNavItems)
  }, [navItems, drillDownLevels, createLevel]);

  const onDrillDown = useCallback((data: Record<string, any>) => {
    if (levelInfo.next) {
      const next = createLevel(levelInfo.next);

      const newNavItem = {
        key: levelInfo.next.key,
        label: formatLabel(data.raw),
        filter: {
          value: formatLabel(data.raw),
          key: navItems[navItems.length - 1].key,
          type: typeof data.raw === 'string' ? 'metricFilter' : 'filter'
        }
      } as const;

      setNavItems([...navItems, newNavItem])
      setLevelInfo(next);
    } else {
      const { raw = [] } = data;
      const filters = navItems.filter(i => i.filter && i.filter.type === 'filter' && !disabledDrillDownKeys?.includes(i.filter.key));
      const payload = filters.map(i => {
        return {
          dimension: i.filter?.key,
          row: { name: [i.filter?.value], value: [i.filter?.value] }
        }
      })

      const lastFilter = filters[filters.length - 1];
      payload.push({
        dimension: lastFilter.key,
        row: { name: raw, value: raw }
      })
      onSelect?.(payload);
    }
  }, [onSelect, levelInfo.next, createLevel, navItems, disabledDrillDownKeys])

  const onReset = useCallback(() => {
    setNavItems([rootNavItem]);
    setLevelInfo(createLevel(drillDownLevels));
    onResetCallback?.();
  }, [createLevel, drillDownLevels, rootNavItem, onResetCallback])

  return {
    navItems,
    levelInfo,
    onNavigate,
    onDrillDown,
    onReset,
  }
}
